import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
} from "recharts";

/* Gecombineerde omzet/marge-staaf: marge = onderdeel van de omzetstaaf, met %-label */
const metMargeDeel = (rows, omzetKey) => (rows || []).map((r) => {
  const omzet = Number(r[omzetKey]) || 0;
  const marge = Number(r.marge) || 0;
  const deel = Math.max(0, Math.min(marge, omzet));
  const pct = omzet ? Math.round((marge / omzet) * 100) : 0;
  return { ...r, marge_deel: deel, omzet_rest: Math.max(0, omzet - deel), marge_pct_label: omzet ? `${pct}%` : "" };
});

const margeTooltipFormatter = (omzetKey) => (v, n, p) => {
  const pl = p?.payload || {};
  if (n === "Omzet") return [fmtEUR2(pl[omzetKey] ?? 0), "Omzet (totaal)"];
  if (n === "Marge") return [`${fmtEUR2(pl.marge ?? 0)}${pl.marge_pct_label ? ` · ${pl.marge_pct_label}` : ""}`, "Marge"];
  return [fmtEUR2(v), n];
};
import {
  Building2, Target, FileText, PhoneCall, PhoneOff, PhoneForwarded, Trophy,
  TrendingUp, TrendingDown, Receipt, CheckCircle2, AlertTriangle, UserPlus,
  Truck, PackageCheck, Package, BarChart3, Megaphone, Layers, History, Boxes, Euro, Clock, Palette, Check, X,
  ShoppingCart, FlaskConical,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmtEUR = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtEUR2 = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const PIE_COLORS = ["#00A5A0", "#5b7c99", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
// Ruime, visueel onderscheidende palette voor de "omzet per maand per verkoopkans"-grafiek.
// Elke klant/verkoopkans krijgt een VASTE kleur (stabiel over alle maanden) zodat je patronen ziet.
const KLANT_COLORS = [
  "#00A5A0", "#5b7c99", "#f59e0b", "#8b5cf6", "#ef4444", "#10b981", "#ec4899",
  "#3b82f6", "#f97316", "#14b8a6", "#a855f7", "#84cc16", "#e11d48", "#0ea5e9",
  "#eab308", "#6366f1", "#22c55e", "#f43f5e", "#06b6d4", "#d946ef", "#65a30d", "#db2777",
];
const OVERIG_COLOR = "#94a3b8"; // neutraal grijs voor de restpost "Overig"

// Eén canonieke klant -> kleur toewijzing die overal op het dashboard gelijk is.
// Bedrijfsbrede overrides (handmatig ingesteld) krijgen voorrang; daarna vullen de
// 10 grootste klanten (op YTD-omzet) aan met vaste palet-kleuren; de rest wordt grijs.
// Zo is bijv. "Pro-DA" in elke grafiek dezelfde kleur.
function bouwKlantKleurMap(d, overrides = {}) {
  const map = {};
  const used = new Set();
  // 1) Bedrijfsbrede overrides eerst — deze klanten krijgen altijd hun vaste kleur.
  Object.entries(overrides || {}).forEach(([naam, hex]) => {
    const n = (naam || "").trim();
    if (n && hex) { map[n] = hex; used.add(hex); }
  });
  // 2) Ranking van klanten op omzet.
  const tot = {};
  (d.omzet_per_maand_kansen || []).forEach((m) => (m.segmenten || []).forEach((s) => {
    const n = s.klant;
    if (!n || String(n).trim().toLowerCase() === "overig") return;
    tot[n] = (tot[n] || 0) + (s.omzet || 0);
  }));
  (d.top_klanten || []).forEach((t) => {
    const n = t.naam;
    if (n && String(n).trim().toLowerCase() !== "overig") tot[n] = Math.max(tot[n] || 0, t.omzet || 0);
  });
  const ranked = Object.keys(tot).sort((a, b) => tot[b] - tot[a]);
  // 3) Vul de top 10 (zonder eigen override) aan met palet-kleuren die nog vrij zijn.
  let auto = 0;
  for (const n of ranked) {
    if (auto >= 10) break;
    if (map[n]) continue; // heeft al een override
    const kleur = KLANT_COLORS.find((c) => !used.has(c));
    if (!kleur) break;
    map[n] = kleur; used.add(kleur); auto += 1;
  }
  return map;
}
// Klanten met omzet, gesorteerd (grootste eerst) — voor het kleuren-beheerscherm.
function klantenOpOmzet(d) {
  const tot = {};
  (d?.omzet_per_maand_kansen || []).forEach((m) => (m.segmenten || []).forEach((s) => {
    const n = s.klant;
    if (!n || String(n).trim().toLowerCase() === "overig") return;
    tot[n] = (tot[n] || 0) + (s.omzet || 0);
  }));
  (d?.top_klanten || []).forEach((t) => {
    const n = t.naam;
    if (n && String(n).trim().toLowerCase() !== "overig") tot[n] = Math.max(tot[n] || 0, t.omzet || 0);
  });
  return Object.entries(tot).sort((a, b) => b[1] - a[1]).map(([naam, omzet]) => ({ naam, omzet }));
}
function kleurVoorKlant(map, naam) {
  const n = String(naam || "");
  if (!n || n.trim().toLowerCase() === "overig") return OVERIG_COLOR;
  return map[n] || OVERIG_COLOR;
}

/* Percentage in het partje zelf; te kleine partjes tonen niets (dan zie je het bij hover) */
const RAD = Math.PI / 180;
const insidePctLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const p = (percent || 0) * 100;
  if (p < 5) return null;
  const r = innerRadius + (outerRadius - innerRadius) * (innerRadius > 0 ? 0.5 : 0.62);
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text x={x} y={y} fill="#ffffff" textAnchor="middle" dominantBaseline="central"
      fontSize={p < 9 ? 11 : 13} fontWeight={600} style={{ pointerEvents: "none" }}>
      {`${p.toFixed(p < 10 ? 1 : 0)}%`}
    </text>
  );
};
const pieTooltip = (data, key) => (v, n) => {
  const totaal = (data || []).reduce((s, x) => s + Math.abs(Number(x[key]) || 0), 0);
  const pct = totaal ? (Math.abs(Number(v) || 0) / totaal) * 100 : 0;
  return [`${fmtEUR2(v)} · ${pct.toFixed(1)}%`, n];
};

const TABS = [
  { key: "algemeen", label: "Algemeen", icon: BarChart3 },
  { key: "facturatie", label: "Facturatie", icon: Receipt },
  { key: "acquisitie", label: "Acquisitie", icon: Megaphone },
  { key: "ecommerce", label: "E-commerce", icon: ShoppingCart },
  { key: "logistiek", label: "Logistiek", icon: Truck },
  { key: "historisch", label: "Historisch", icon: History },
  { key: "huidtest", label: "Huidtest", icon: FlaskConical },
];

const CAT_COLORS = ["#00A5A0", "#5b7c99", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

function StatCard({ icon: Icon, label, value, sub, accent, to, testid }) {
  const inner = (
    <>
      <Icon className={`h-5 w-5 ${accent ? "text-accent" : "text-muted-foreground group-hover:text-primary transition-colors"}`} />
      <div className="mt-6">
        <div className="font-display text-4xl font-medium">{value}</div>
        <div className="text-sm text-muted-foreground mt-1">{label}</div>
        {sub != null && <div className="text-sm text-primary font-medium mt-1">{sub}</div>}
      </div>
    </>
  );
  return to
    ? <Link to={to} className="stat-card block group" data-testid={testid}>{inner}</Link>
    : <div className="stat-card group" data-testid={testid}>{inner}</div>;
}

function Panel({ title, children, action, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-2xl font-medium">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

/* ---------------- ALGEMEEN ---------------- */
function Algemeen({ d, jaar, vw, merk, klantKleur, onKleuren }) {
  const delta = d.omzet_vorig_jaar > 0 ? ((d.omzet_dit_jaar - d.omzet_vorig_jaar) / d.omzet_vorig_jaar) * 100 : null;
  const up = (delta ?? 0) >= 0;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Leidende weergave: gefactureerde omzet YTD (op factuurdatum). */}
        <div className="stat-card group lg:col-span-2" data-testid="alg-omzet-ytd">
          <BarChart3 className="h-5 w-5 text-primary" />
          <div className="mt-6">
            <div className="font-display text-4xl font-medium">{fmtEUR(d.omzet_gefactureerd_ytd ?? d.omzet_dit_jaar)}</div>
            <div className="text-sm text-muted-foreground mt-1">Gefactureerd YTD {jaar} <span className="text-muted-foreground/70">· op factuurdatum</span></div>
            {delta != null && (
              <div className={`text-sm font-medium mt-1 inline-flex items-center gap-1 ${up ? "text-emerald-500" : "text-destructive"}`}>
                {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                {up ? "+" : ""}{delta.toFixed(1)}% vs {jaar - 1}
              </div>
            )}
            {d.omzet_nog_te_factureren > 0 && (
              <div className="mt-4 pt-4 border-t border-border/60 grid grid-cols-2 gap-4">
                <Link to="/verkoopkansen?fase=Deal!" className="group/subs block" data-testid="alg-nog-te-factureren">
                  <div className="text-2xl font-display font-medium tabular-nums text-amber-700 group-hover/subs:underline">
                    + {fmtEUR(d.omzet_nog_te_factureren)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Nog te factureren <span className="text-muted-foreground/70">({d.omzet_nog_te_factureren_aantal} deals)</span>
                  </div>
                </Link>
                <div data-testid="alg-realistisch-totaal">
                  <div className="text-2xl font-display font-medium tabular-nums text-primary">
                    = {fmtEUR(d.omzet_realistisch_totaal ?? (d.omzet_gefactureerd_ytd + d.omzet_nog_te_factureren))}
                  </div>
                  <div className="text-xs text-muted-foreground">Realistisch totaal</div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="stat-card group" data-testid="alg-marge-ytd">
          <TrendingUp className="h-5 w-5 text-emerald-500" />
          <div className="mt-6">
            <div className="font-display text-4xl font-medium">{fmtEUR(d.marge_dit_jaar)}</div>
            <div className="text-sm text-muted-foreground mt-1">Marge YTD {jaar}</div>
            <div className="text-sm font-medium mt-1 inline-flex items-center gap-1 text-emerald-500">
              {d.marge_pct}% marge
            </div>
          </div>
        </div>
        <StatCard icon={BarChart3} label={`Omzet YTD ${jaar - 1}`} value={fmtEUR(d.omzet_vorig_jaar)} testid="alg-omzet-vorig" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard icon={Building2} label="Klanten" value={d.aantal_klanten} sub={merk ? "niet merk-specifiek" : undefined} to="/organisaties?status=klant" testid="alg-klanten" />
        <StatCard icon={Target} label="Open verkoopkansen" value={d.open_kansen_aantal} sub={merk ? `${fmtEUR(d.open_kansen_bedrag)} · niet merk-specifiek` : fmtEUR(d.open_kansen_bedrag)} to="/verkoopkansen" testid="alg-kansen" />
      </div>

      {vw && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="stat-card group" data-testid="alg-voorraadwaarde">
            <Boxes className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            <div className="mt-6">
              <div className="font-display text-4xl font-medium">{fmtEUR(vw.inkoopwaarde)}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Voorraadwaarde (inkoop) <span className="text-muted-foreground/70">· {vw.stuks.toLocaleString("nl-NL")} stuks</span>
              </div>
              <div className="text-sm text-primary font-medium mt-1">{fmtEUR(vw.verkoopwaarde)} op verkoopprijs</div>
              {vw.china_stuks > 0 && (
                <div className="mt-4 pt-4 border-t border-border/60" data-testid="alg-voorraadwaarde-china">
                  <div className="text-2xl font-display font-medium tabular-nums text-amber-700">
                    + {fmtEUR(vw.china_inkoopwaarde)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Order bij de fabrikant · {vw.china_stuks.toLocaleString("nl-NL")} stuks ·{" "}
                    {fmtEUR(vw.china_verkoopwaarde)} op verkoopprijs
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="stat-card group" data-testid="alg-voorraad-verkoopwaarde">
            <Euro className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            <div className="mt-6">
              <div className="font-display text-4xl font-medium">{fmtEUR(vw.gem_verkoopwaarde)}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Voorraad × gem. verkoopwaarde
              </div>
              <div className="text-sm text-primary font-medium mt-1">
                {fmtEUR2(vw.gem_verkoopprijs_stuk)} gemiddeld per stuk (per artikel, laatste 36 mnd)
              </div>
              {vw.china_stuks > 0 && (
                <div className="mt-4 pt-4 border-t border-border/60">
                  <div className="text-2xl font-display font-medium tabular-nums text-amber-700">
                    + {fmtEUR(vw.china_gem_verkoopwaarde)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Order bij de fabrikant tegen dezelfde gemiddelde prijs
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Panel title="Omzet & marge per maand (YTD vs vorig jaar)" testid="alg-omzet-grafiek">
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={metMargeDeel(d.omzet_per_maand, "dit_jaar")} margin={{ top: 20, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="maand" stroke="hsl(var(--muted-foreground))" fontSize={12} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={fmtEUR} width={70} />
            <Tooltip formatter={margeTooltipFormatter("dit_jaar")} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Legend />
            <Bar dataKey="marge_deel" name="Marge" stackId="om" fill="#10b981" />
            <Bar dataKey="omzet_rest" name="Omzet" stackId="om" fill="#00A5A0" radius={[6, 6, 0, 0]}>
              <LabelList dataKey="marge_pct_label" position="top" fontSize={11} fill="hsl(var(--muted-foreground))" />
            </Bar>
            <Bar dataKey="vorig_jaar" name={`Omzet ${jaar - 1}`} fill="#94a3b8" radius={[6, 6, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </Panel>

      <Panel title="Omzet per maand per verkoopkans (YTD)" testid="alg-maand-kansen"
        action={
          <button onClick={onKleuren} data-testid="btn-klantkleuren"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Palette className="h-4 w-4" /> Kleuren
          </button>
        }>
        {(d.omzet_per_maand_kansen || []).some((m) => (m.segmenten || []).length) ? (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              De <strong>10 grootste klanten</strong> hebben elk een vaste kleur over alle maanden; overige klanten zijn grijs. Elk blok is één verkoopkans; beweeg erover voor omzet en marge (met %), klik om naar de verkoopkans te gaan.
            </p>
            <MaandKansenChart data={d.omzet_per_maand_kansen} kleurMap={klantKleur} />
          </>
        ) : <Empty label="Nog geen gefactureerde omzet per verkoopkans dit jaar." />}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Omzet per categorie (YTD)" testid="alg-categorie">

          {d.omzet_per_categorie?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={d.omzet_per_categorie} dataKey="omzet" nameKey="categorie" cx="50%" cy="50%" outerRadius={100}
                  label={insidePctLabel} labelLine={false} data-testid="pie-categorie">
                  {d.omzet_per_categorie.map((c, i) => <Cell key={c.categorie || i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={pieTooltip(d.omzet_per_categorie, "omzet")} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty label="Nog geen gecategoriseerde omzet. Stel categorieën in bij producten." />}
        </Panel>

        <Panel title="Top klanten (YTD)" testid="alg-top-klanten">
          {d.top_klanten?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={d.top_klanten} dataKey="omzet" nameKey="naam" cx="50%" cy="50%" innerRadius={50} outerRadius={100}
                  label={insidePctLabel} labelLine={false} data-testid="pie-top-klanten">
                  {d.top_klanten.map((k, i) => <Cell key={k.naam || i} fill={kleurVoorKlant(klantKleur, k.naam)} />)}
                </Pie>
                <Tooltip formatter={pieTooltip(d.top_klanten, "omzet")} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty label="Nog geen omzet dit jaar." />}
        </Panel>
      </div>

      <Panel title={`Top 5 producten (YTD, vs ${jaar - 1})`} testid="alg-top-producten">
        <div className="divide-y divide-border">
          {(d.top_producten || []).map((p, i) => (
            <div key={p.code || i} className="flex items-center justify-between py-3" data-testid={`alg-prod-${i}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-display text-lg text-muted-foreground w-6">{i + 1}</span>
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.product}</div>
                  {p.code && <div className="text-xs text-muted-foreground font-mono">{p.code}</div>}
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-right">
                  <div className="font-medium">{fmtEUR2(p.omzet)}</div>
                  <div className="text-xs text-muted-foreground">{p.aantal} stuks</div>
                </div>
                <div className="text-right w-24" data-testid={`alg-prod-${i}-ytd`}>
                  <div className={`text-sm font-medium ${p.delta_pct == null ? "text-muted-foreground" : p.delta_pct >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                    {p.delta_pct == null ? "nieuw" : `${p.delta_pct > 0 ? "+" : ""}${p.delta_pct}%`}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{jaar - 1}: {fmtEUR(p.vorig_jaar || 0)}</div>
                </div>
              </div>
            </div>
          ))}
          {(!d.top_producten || d.top_producten.length === 0) && <Empty label="Nog geen verkoopdata dit jaar." />}
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StijgersDalersPanel titel="Top 20 stijgers (omzet vs vorig jaar)" items={d.omzet_stijgers} positief testid="alg-stijgers" />
        <StijgersDalersPanel titel="Top 20 dalers (omzet vs vorig jaar)" items={d.omzet_dalers} testid="alg-dalers" />
      </div>
    </div>
  );
}

/* Top 20 stijgers/dalers qua omzet t.o.v. dezelfde periode vorig jaar */
function StijgersDalersPanel({ titel, items, positief, testid }) {
  const lijst = items || [];
  return (
    <Panel title={titel} testid={testid}>
      <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
        {lijst.map((k, i) => (
          <Link key={k.relatie_id} to={k.relatie_id === "bol.com" ? "/bol" : `/organisaties/${k.relatie_id}`}
            className="flex items-center justify-between py-2.5 hover:bg-secondary/40 rounded-md px-2 -mx-2"
            data-testid={`${testid}-rij-${i}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-display text-sm text-muted-foreground w-6 shrink-0">{i + 1}</span>
              {positief ? <TrendingUp className="h-4 w-4 text-emerald-500 shrink-0" /> : <TrendingDown className="h-4 w-4 text-destructive shrink-0" />}
              <div className="min-w-0">
                <div className="font-medium truncate text-sm">{k.naam}</div>
                <div className="text-xs text-muted-foreground">{fmtEUR2(k.vorig_jaar)} → {fmtEUR2(k.dit_jaar)}</div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={`font-medium text-sm ${positief ? "text-emerald-600" : "text-destructive"}`}>
                {positief ? "+" : ""}{fmtEUR2(k.delta)}
              </div>
              <div className="text-xs text-muted-foreground">
                {k.delta_pct == null ? "nieuw" : `${k.delta_pct > 0 ? "+" : ""}${k.delta_pct}%`}
              </div>
            </div>
          </Link>
        ))}
        {lijst.length === 0 && <Empty label={positief ? "Nog geen stijgers." : "Geen dalers — mooi zo!"} />}
      </div>
    </Panel>
  );
}

/* ---------------- FACTURATIE ---------------- */
function Facturatie({ d }) {
  const w = d.wachtrij || {};
  const aging = d.ouderdom || [];
  const agingColors = { "0-30": "#10b981", "30-60": "#f59e0b", "60+": "#ef4444" };
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/verkoopfacturen" className="stat-card block group" data-testid="fac-openstaand">
          <Receipt className="h-5 w-5 text-amber-500" />
          <div className="mt-6">
            <div className="font-display text-3xl font-medium">{fmtEUR(d.openstaand?.bedrag)}</div>
            <div className="text-sm text-muted-foreground mt-1">Openstaand</div>
            <div className="text-sm text-primary font-medium mt-1">{d.openstaand?.aantal} facturen</div>
          </div>
        </Link>
        <Link to="/verkoopfacturen" className="stat-card block group" data-testid="fac-vervallen">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <div className="mt-6">
            <div className="font-display text-3xl font-medium">{fmtEUR(d.vervallen?.bedrag)}</div>
            <div className="text-sm text-muted-foreground mt-1">Vervallen / te laat</div>
            <div className="text-sm text-destructive font-medium mt-1">{d.vervallen?.aantal} facturen</div>
          </div>
        </Link>
        <Link to="/verkoopfacturen" className="stat-card block group" data-testid="fac-betaald-ytd">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <div className="mt-6">
            <div className="font-display text-3xl font-medium">{fmtEUR(d.betaald_ytd?.bedrag)}</div>
            <div className="text-sm text-muted-foreground mt-1">Betaald (YTD)</div>
            <div className="text-sm text-primary font-medium mt-1">{d.betaald_ytd?.aantal} facturen</div>
          </div>
        </Link>
        <div className="stat-card" data-testid="fac-betaaltermijn">
          <Clock className="h-5 w-5 text-sky-500" />
          <div className="mt-6">
            <div className="font-display text-3xl font-medium">{d.gem_betaaltermijn_dagen ?? 0} <span className="text-lg text-muted-foreground">dagen</span></div>
            <div className="text-sm text-muted-foreground mt-1">Gem. betaaltermijn</div>
            <div className="text-sm text-muted-foreground mt-1">factuur → betaling (YTD)</div>
          </div>
        </div>
      </div>

      <Panel title="Wachtrij — actie nodig" testid="fac-wachtrij">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { key: "concept", label: "Concept (nog te versturen)", icon: FileText, val: w.concept, tone: "text-muted-foreground" },
            { key: "herinnering", label: "Herinnering loopt", icon: AlertTriangle, val: w.herinnering, tone: "text-amber-500" },
            { key: "vervallen", label: "Vervallen / te laat", icon: AlertTriangle, val: w.vervallen, tone: "text-destructive" },
          ].map(({ key, label, icon: Icon, val, tone }) => (
            <Link key={key} to="/verkoopfacturen" className="flex items-center gap-3 rounded-lg border border-border p-4 hover:border-primary/40 transition-colors" data-testid={`fac-wachtrij-${key}`}>
              <Icon className={`h-6 w-6 ${tone}`} />
              <div>
                <div className="font-display text-2xl font-medium">{val ?? 0}</div>
                <div className="text-xs text-muted-foreground">{label}</div>
              </div>
            </Link>
          ))}
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Openstaand per ouderdom (dagen)" testid="fac-ouderdom">
          {aging.some((a) => a.aantal > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={aging} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="categorie" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={fmtEUR} width={64} />
                <Tooltip formatter={(v, n, p) => [`${fmtEUR2(v)} · ${p.payload.aantal} facturen`, "Openstaand"]}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Bar dataKey="bedrag" name="Openstaand" radius={[6, 6, 0, 0]}>
                  {aging.map((a, i) => <Cell key={a.categorie || i} fill={agingColors[a.categorie] || "#00A5A0"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty label="Geen openstaande facturen." />}
        </Panel>

        <Panel title="Facturen per status" testid="fac-per-status">
          {d.per_status?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={d.per_status} layout="vertical" margin={{ left: 40, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={fmtEUR} />
                <YAxis type="category" dataKey="status" stroke="hsl(var(--muted-foreground))" fontSize={11} width={150} />
                <Tooltip formatter={(v, n) => n === "bedrag" ? fmtEUR2(v) : v} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Bar dataKey="bedrag" name="Bedrag" radius={[0, 6, 6, 0]}>
                  {d.per_status.map((s, i) => <Cell key={s.status || i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty label="Nog geen facturen." />}
        </Panel>
      </div>
    </div>
  );
}

/* ---------------- ACQUISITIE ---------------- */
function Acquisitie({ d, belVandaag }) {
  const bv = belVandaag || {};
  const doel = bv.beldoel || 20;
  const pct = doel ? Math.min(100, Math.round((bv.totaal / doel) * 100)) : 0;
  const behaald = bv.totaal >= doel;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={PhoneCall} label="Bel-starts (YTD)" value={d.bel_starts} to="/belactiviteit" testid="acq-bel-starts" />
        <StatCard icon={Target} label="Kansen aangemaakt (YTD)" value={d.kansen_aangemaakt} to="/verkoopkansen" testid="acq-kansen" />
        <StatCard icon={UserPlus} label="Nieuwe klanten (YTD)" value={d.nieuwe_klanten} to="/organisaties?status=klant" testid="acq-nieuwe-klanten" />
      </div>

      <Panel title="Vandaag gebeld" action={<Link to="/instellingen/beldoel" className="text-sm text-primary hover:underline">Beldoel →</Link>} testid="acq-vandaag">
        <Link to="/instellingen/beldoel" className="block rounded-lg border border-border p-4 mb-4 hover:border-primary/40 transition-colors" data-testid="acq-beldoel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" /> Dagdoel
              {behaald && <Badge className="bg-accent text-accent-foreground">Behaald! 🎉</Badge>}
            </div>
            <div className="text-sm text-muted-foreground">
              <span className="font-display text-base text-foreground">{bv.totaal ?? 0}</span> / {doel} belletjes
            </div>
          </div>
          <Progress value={pct} className={behaald ? "[&>div]:bg-accent" : ""} />
        </Link>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: "totaal", label: "Gebeld vandaag", icon: PhoneCall },
            { key: "geen_gehoor", label: "Geen gehoor", icon: PhoneOff },
            { key: "later_terugbellen", label: "Later terugbellen", icon: PhoneForwarded },
            { key: "deals", label: "Deals vandaag", icon: Trophy },
          ].map(({ key, label, icon: Icon }) => (
            <Link key={key} to="/verkoopkansen" className="stat-card block group" data-testid={`acq-bel-${key}`}>
              <Icon className={`h-5 w-5 transition-colors ${key === "deals" ? "text-accent" : "text-muted-foreground group-hover:text-primary"}`} />
              <div className="mt-6">
                <div className="font-display text-4xl font-medium">{bv[key] ?? 0}</div>
                <div className="text-sm text-muted-foreground mt-1">{label}</div>
              </div>
            </Link>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/* ---------------- LOGISTIEK ---------------- */
function Logistiek({ d }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard icon={PackageCheck} label="Inkomende zendingen (YTD)" value={d.inkomend} to="/inkooporders" testid="log-inkomend" />
        <StatCard icon={Truck} label="Uitgaande zendingen (YTD)" value={d.uitgaand} to="/pakbonnen" testid="log-uitgaand" />
      </div>
      <Panel title="Zendingen per maand" testid="log-grafiek">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={d.per_maand} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="maand" stroke="hsl(var(--muted-foreground))" fontSize={12} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Legend />
            <Bar dataKey="inkomend" name="Inkomend" fill="#5b7c99" radius={[6, 6, 0, 0]} />
            <Bar dataKey="uitgaand" name="Uitgaand" fill="#00A5A0" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}

/* ---------------- HISTORISCH ---------------- */
function Omloopsnelheid({ merk }) {
  const [o, setO] = useState(null);
  useEffect(() => {
    setO(null);
    api.get("/dashboard/omloopsnelheid", { params: merk ? { merk } : {} })
      .then((r) => setO(r.data)).catch(() => {});
  }, [merk]);
  if (!o) return null;
  const jaren = (o.per_jaar || []).filter((r) => r.omloopsnelheid != null);
  const maanden = (o.per_maand || []).filter((r) => r.omloopsnelheid != null);
  const l12 = o.laatste12 || {};
  const traagste = (o.per_product || []).slice(0, 10);
  const dagen = (n) => (n == null || n > 3650 ? "—" : `${new Intl.NumberFormat("nl-NL").format(n)} dagen`);
  const tip = (v, n, p) => {
    const pl = p?.payload || {};
    if (n === "omloopsnelheid") return [`${v}× per jaar · ${dagen(pl.dagen_voorraad)} voorraad`, "Omloopsnelheid"];
    return [fmtEUR2(v), n === "cogs" ? "Inkoopwaarde verkocht" : "Gem. voorraadwaarde"];
  };
  return (
    <Panel title="Omloopsnelheid van de voorraad" testid="hist-omloopsnelheid"
      action={l12.omloopsnelheid != null ? (
        <span className="text-sm text-muted-foreground">
          Laatste 12 mnd <span className="font-display text-base text-primary">{l12.omloopsnelheid}×</span>{" "}
          · {dagen(l12.dagen_voorraad)}
        </span>
      ) : null}>
      <p className="text-xs text-muted-foreground mb-4">
        Inkoopwaarde van het verkochte gedeeld door de gemiddelde voorraadwaarde. De historische voorraad komt uit je
        geïmporteerde metingen (ankerpunt) plus verkopen en geregistreerde inboekingen. Verkopen aan klanten die
        direct uit de binnenkomende zending gaan tellen niet mee. Vanaf {o.vanaf}.
      </p>
      {jaren.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={jaren} margin={{ top: 20, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="jaar" stroke="hsl(var(--muted-foreground))" fontSize={12} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} width={44}
              tickFormatter={(v) => `${v}×`} />
            <Tooltip formatter={tip} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Bar dataKey="omloopsnelheid" name="omloopsnelheid" fill="#00A5A0" radius={[6, 6, 0, 0]}>
              <LabelList dataKey="omloopsnelheid" position="top" fontSize={11} formatter={(v) => `${v}×`}
                fill="hsl(var(--muted-foreground))" />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      ) : <Empty label="Nog geen historische voorraadstanden om op te rekenen." />}
      {maanden.length > 1 && (
        <div className="mt-6 pt-6 border-t border-border/60">
          <div className="text-sm text-muted-foreground mb-2">Per maand (geannualiseerd)</div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={maanden} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11}
                interval={Math.max(0, Math.ceil(maanden.length / 14) - 1)} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} width={44} tickFormatter={(v) => `${v}×`} />
              <Tooltip formatter={tip} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Bar dataKey="omloopsnelheid" name="omloopsnelheid" fill="#00A5A0" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      {maanden.length > 1 && (
        <div className="mt-6 pt-6 border-t border-border/60" data-testid="hist-voorraadwaarde-verloop">
          <div className="text-sm text-muted-foreground mb-2">Voorraadwaarde per maand (inkoopwaarde, einde maand)</div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={o.per_maand} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="vwGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00A5A0" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#00A5A0" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11}
                interval={Math.max(0, Math.ceil(o.per_maand.length / 14) - 1)} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} width={70} tickFormatter={fmtEUR} />
              <Tooltip formatter={(v) => [fmtEUR2(v), "Voorraadwaarde"]}
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Bar dataKey="voorraadwaarde" name="Voorraadwaarde" fill="#00A5A0" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      {traagste.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border/60" data-testid="hist-omloop-traagste">
          <div className="text-sm text-muted-foreground mb-1">Hier staat je geld het langst stil</div>
          <p className="text-xs text-muted-foreground mb-3">
            Traagste omloop van de laatste 12 maanden, alleen producten waar nú voorraad in zit.
          </p>
          <div className="divide-y divide-border">
            {traagste.map((r) => (
              <div key={r.product_id} className="flex items-center gap-3 py-2" data-testid={`omloop-prod-${r.sku}`}>
                <Link to={`/producten/${r.product_id}`}
                  className="min-w-0 flex-1 text-sm hover:text-primary transition-colors truncate">
                  <span className="font-mono text-xs text-muted-foreground">{r.sku}</span> {r.naam}
                </Link>
                <span className="text-xs text-muted-foreground shrink-0 w-44 text-right">
                  {new Intl.NumberFormat("nl-NL").format(r.huidige_stuks)} stuks · {fmtEUR(r.huidige_voorraadwaarde)}
                </span>
                <span className="text-sm font-display shrink-0 w-16 text-right">
                  {r.omloopsnelheid ? `${r.omloopsnelheid}×` : "0×"}
                </span>
                <span className="text-xs text-muted-foreground shrink-0 w-28 text-right">
                  {r.omloopsnelheid ? dagen(r.dagen_voorraad) : "geen verkoop"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

    </Panel>
  );
}

function Historisch({ merk }) {
  const [h, setH] = useState(null);
  useEffect(() => {
    setH(null);
    api.get("/dashboard/historisch", { params: merk ? { merk } : {} })
      .then((r) => setH(r.data)).catch(() => {});
  }, [merk]);
  if (!h) return <div className="text-muted-foreground text-sm">Laden…</div>;
  const fmtNum = (n) => new Intl.NumberFormat("nl-NL").format(Number(n) || 0);
  return (
    <div className="space-y-8" data-testid="historisch-content">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Boxes} label="Totaal verkochte stuks" value={fmtNum(h.totaal_verkocht)} testid="hist-stuks" />
        <StatCard icon={Euro} label="Totale omzet" value={fmtEUR(h.totale_omzet)} testid="hist-omzet" />
        <StatCard icon={Receipt} label="Aantal verkoopfacturen" value={fmtNum(h.totaal_facturen)} testid="hist-facturen" />
      </div>

      <Panel title="Omzet & marge per jaar" testid="hist-omzet-marge-grafiek"
        action={<span className="text-sm text-muted-foreground">Gem. marge <span className="font-display text-base text-emerald-500">{h.marge_pct_totaal}%</span></span>}>
        {h.omzet_marge_per_jaar?.length ? (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={metMargeDeel(h.omzet_marge_per_jaar, "omzet")} margin={{ top: 20, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="jaar" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={fmtEUR} width={70} />
              <Tooltip
                formatter={margeTooltipFormatter("omzet")}
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="marge_deel" name="Marge" stackId="om" fill="#10b981" />
              <Bar dataKey="omzet_rest" name="Omzet" stackId="om" fill="#00A5A0" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="marge_pct_label" position="top" fontSize={11} fill="hsl(var(--muted-foreground))" />
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        ) : <Empty label="Nog geen marge-data." />}
      </Panel>

      <Omloopsnelheid merk={merk} />

      <Panel title="Omzet per categorie per jaar" testid="hist-categorie-grafiek">
        {h.omzet_per_categorie_per_jaar?.length ? (
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={h.omzet_per_categorie_per_jaar} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="jaar" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={fmtEUR} width={70} />
              <Tooltip formatter={(v) => fmtEUR2(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Legend />
              {(h.categorieen || []).map((c, i) => (
                <Bar key={c} dataKey={c} name={c} stackId="a" fill={CAT_COLORS[i % CAT_COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty label="Nog geen historische omzet." />}
      </Panel>

      <Panel title="Top 5 producten (aller tijden, in stuks)" testid="hist-top5">
        <div className="divide-y divide-border">
          {(h.top5_producten || []).map((p, i) => (
            <div key={p.code || i} className="flex items-center justify-between py-3" data-testid={`hist-prod-${i}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-display text-lg text-muted-foreground w-6">{i + 1}</span>
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.product}</div>
                  {p.code && <div className="text-xs text-muted-foreground font-mono">{p.code}</div>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-medium">{fmtNum(p.aantal)} stuks</div>
              </div>
            </div>
          ))}
          {(!h.top5_producten || h.top5_producten.length === 0) && <Empty label="Nog geen verkoopdata." />}
        </div>
      </Panel>
    </div>
  );
}

function MaandKansenChart({ data, kleurMap = {} }) {
  const navigate = useNavigate();
  const [tip, setTip] = useState(null);
  const maxTotal = Math.max(1, ...data.map((m) => m.totaal || 0));
  const TRACK = 240;

  // Kleuren komen uit de gedeelde, dashboard-brede klant->kleur map.
  const kleurKey = (s) => String(s.klant || "Overig");
  const totals = useMemo(() => {
    const t = {};
    data.forEach((m) => (m.segmenten || []).forEach((s) => {
      const n = kleurKey(s);
      t[n] = (t[n] || 0) + (s.omzet || 0);
    }));
    return t;
  }, [data]);
  const orderedKeys = Object.keys(kleurMap).sort((a, b) => (totals[b] || 0) - (totals[a] || 0));
  const rankOf = {};
  orderedKeys.forEach((k, i) => { rankOf[k] = i; });
  const heeftOverig = useMemo(
    () => data.some((m) => (m.segmenten || []).some((s) => !(kleurKey(s) in kleurMap))),
    [data, kleurMap]
  );

  return (
    <div className="relative">
      <div className="flex items-end gap-2 sm:gap-3" style={{ height: TRACK + 26 }} data-testid="maand-kansen-chart">
        {data.map((m) => {
          const colH = Math.round(((m.totaal || 0) / maxTotal) * TRACK);
          const segs = [...(m.segmenten || [])].sort(
            (a, b) => (rankOf[kleurKey(a)] ?? 9999) - (rankOf[kleurKey(b)] ?? 9999)
          );
          return (
            <div key={m.maand_num} className="flex-1 flex flex-col items-center justify-end gap-1.5 min-w-0">
              <div className="w-full flex flex-col justify-end rounded-t-md overflow-hidden" style={{ height: TRACK }}>
                {segs.map((s, i) => {
                  const h = m.totaal > 0 ? Math.max(2, Math.round((s.omzet / m.totaal) * colH)) : 0;
                  const clickable = !!s.verkoopkans_id;
                  return (
                    <div
                      key={kleurKey(s) + i}
                      data-testid={`maandstaaf-${m.maand_num}-${i}`}
                      onMouseEnter={(e) => setTip({ seg: s, rect: e.currentTarget.getBoundingClientRect() })}
                      onMouseLeave={() => setTip(null)}
                      onClick={() => clickable && navigate(`/verkoopkansen/${s.verkoopkans_id}`)}
                      className={`w-full transition-opacity hover:opacity-75 ${clickable ? "cursor-pointer" : "cursor-default"}`}
                      style={{ height: h, background: kleurVoorKlant(kleurMap, s.klant) }}
                    />
                  );
                })}
              </div>
              <div className="text-[11px] text-muted-foreground tabular-nums">{m.maand}</div>
            </div>
          );
        })}
      </div>

      {(orderedKeys.length > 0 || heeftOverig) && (
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5" data-testid="maand-kansen-legenda">
          {orderedKeys.map((k) => (
            <div
              key={k}
              className="flex items-center gap-1.5 text-xs cursor-default"
              data-testid={`maand-kansen-legenda-item-${k}`}
              title={k}
            >
              <span className="inline-block h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: kleurMap[k] }} />
              <span className="text-muted-foreground truncate max-w-[150px]">{k}</span>
            </div>
          ))}
          {heeftOverig && (
            <div
              className="flex items-center gap-1.5 text-xs cursor-default"
              data-testid="maand-kansen-legenda-item-overig"
              title="Overige klanten"
            >
              <span className="inline-block h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: OVERIG_COLOR }} />
              <span className="text-muted-foreground">Overige klanten</span>
            </div>
          )}
        </div>
      )}
      {tip && (
        <div
          className="fixed z-50 pointer-events-none bg-card border border-border rounded-md shadow-lg px-3 py-2 text-xs"
          style={{ left: tip.rect.left + tip.rect.width / 2, top: tip.rect.top - 8, transform: "translate(-50%,-100%)" }}
          data-testid="maandstaaf-tooltip"
        >
          <div className="font-medium mb-0.5">{tip.seg.klant}</div>
          <div>Omzet: <span className="font-medium tabular-nums">{fmtEUR2(tip.seg.omzet)}</span></div>
          <div>Marge: <span className="font-medium tabular-nums">{fmtEUR2(tip.seg.marge)}</span> ({tip.seg.marge_pct}%)</div>
          {tip.seg.verkoopkans_id ? <div className="text-primary mt-0.5">Klik → naar verkoopkans</div> : null}
        </div>
      )}
    </div>
  );
}


function Empty({ label }) {
  return <div className="p-8 text-center text-muted-foreground text-sm flex flex-col items-center gap-2"><Layers className="h-6 w-6 opacity-40" />{label}</div>;
}

function KlantKleurenDialog({ open, onClose, d, overrides, onSaved }) {
  const [draft, setDraft] = useState(overrides || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setDraft(overrides || {}); }, [open, overrides]);
  const klanten = useMemo(() => klantenOpOmzet(d), [d]);
  const kies = (naam, kleur) => setDraft((prev) => {
    const next = { ...prev };
    if (next[naam] === kleur) delete next[naam]; // nogmaals klikken = wissen
    else next[naam] = kleur;
    return next;
  });
  const wis = (naam) => setDraft((prev) => { const n = { ...prev }; delete n[naam]; return n; });
  const opslaan = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/instellingen/klant-kleuren", { overrides: draft });
      onSaved(data?.overrides || draft);
      toast.success("Klantkleuren opgeslagen");
      onClose();
    } catch {
      toast.error("Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="klantkleuren-dialog">
        <DialogHeader>
          <DialogTitle>Klantkleuren</DialogTitle>
          <DialogDescription>
            Koppel klanten aan een vaste kleur; die kleur wordt overal op het dashboard gebruikt.
            Klanten zonder eigen kleur krijgen automatisch een kleur (top 10) of grijs.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto -mx-2 px-2 divide-y divide-border">
          {klanten.length === 0 && (
            <div className="py-6 text-sm text-muted-foreground text-center">Nog geen klanten met omzet dit jaar.</div>
          )}
          {klanten.map(({ naam }) => (
            <div key={naam} className="py-3" data-testid={`kleur-rij-${naam}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-sm font-medium truncate">{naam}</span>
                {draft[naam] && (
                  <button onClick={() => wis(naam)} data-testid={`kleur-auto-${naam}`}
                    className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0">
                    <X className="h-3 w-3" /> Auto
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {KLANT_COLORS.map((c) => {
                  const actief = draft[naam] === c;
                  return (
                    <button key={c} onClick={() => kies(naam, c)}
                      data-testid={`kleur-swatch-${naam}-${c}`} title={c}
                      className={`h-6 w-6 rounded-md border flex items-center justify-center transition-transform ${actief ? "ring-2 ring-offset-1 ring-foreground scale-110" : "border-border hover:scale-105"}`}
                      style={{ background: c }}>
                      {actief ? <Check className="h-3.5 w-3.5 text-white" /> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="kleur-annuleer">Annuleren</Button>
          <Button onClick={opslaan} disabled={saving} data-testid="kleur-opslaan">{saving ? "Opslaan…" : "Opslaan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ===================== E-commerce & Huidtest ===================== */

const PERIODES_UI = [
  { k: "7d", l: "7 dagen" }, { k: "28d", l: "28 dagen" },
  { k: "90d", l: "90 dagen" }, { k: "365d", l: "365 dagen" },
];
const KANAAL_KLEUR = { bol: "#0b63f6", webshop: "#00A5A0", frankly: "#111827", nextbrush: "#8b5cf6" };
const KANAAL_TESTID = { bol: "bol", webshop: "webshop", frankly: "frankly", nextbrush: "nextbrush" };

function PeriodeKiezer({ periode, setPeriode, testid }) {
  return (
    <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid={testid}>
      {PERIODES_UI.map((p) => (
        <button key={p.k} onClick={() => setPeriode(p.k)} data-testid={`${testid}-${p.k}`}
          className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
            periode === p.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          {p.l}
        </button>
      ))}
    </div>
  );
}

function Verschil({ nu, vorige }) {
  if (nu == null || vorige == null) return null;
  if (!vorige) return nu > 0 ? <span className="text-xs text-emerald-600">nieuw</span> : null;
  const diff = ((nu - vorige) / Math.abs(vorige)) * 100;
  const up = diff >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs mt-0.5 ${up ? "text-emerald-600" : "text-red-500"}`}>
      <Icon className="h-3 w-3" /> {Math.abs(diff).toFixed(0)}%
    </span>
  );
}

function KanaalKaart({ k }) {
  const kleur = KANAAL_KLEUR[k.kanaal] || "#64748b";
  const testid = `ecommerce-kaart-${KANAAL_TESTID[k.kanaal] || k.kanaal}`;
  if (!k.gekoppeld) {
    return (
      <div data-testid={testid} className="rounded-xl border border-dashed border-border bg-secondary/40 p-5">
        <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: kleur }} /><span className="font-medium">{k.naam}</span></div>
        <div className="text-sm text-muted-foreground mt-3">{k.melding || "Nog niet gekoppeld."}</div>
      </div>
    );
  }
  const isNB = k.kanaal === "nextbrush";
  const cijfers = isNB ? [
    { label: k.bezoekers_label, val: (k.bezoekers ?? 0).toLocaleString("nl-NL"), nu: k.bezoekers, vor: k.vorige?.bezoekers },
    { label: "Klikken naar bol.com", val: (k.extra?.klikken_bol ?? 0).toLocaleString("nl-NL") },
    { label: "Doorklikratio", val: k.extra?.doorklikratio != null ? `${k.extra.doorklikratio}%` : "—" },
  ] : [
    { label: k.bezoekers_label, val: (k.bezoekers ?? 0).toLocaleString("nl-NL"), nu: k.bezoekers, vor: k.vorige?.bezoekers },
    { label: "Orders", val: (k.orders ?? 0).toLocaleString("nl-NL"), nu: k.orders, vor: k.vorige?.orders },
    { label: "Conversie", val: k.conversie != null ? `${k.conversie}%` : "—", nu: k.conversie, vor: k.vorige?.conversie },
    { label: "Omzet excl. btw", val: fmtEUR(k.omzet_excl_btw || 0), nu: k.omzet_excl_btw, vor: k.vorige?.omzet_excl_btw },
  ];
  return (
    <div data-testid={testid} className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: kleur }} /><span className="font-medium">{k.naam}</span></div>
        {isNB && <Badge variant="secondary" className="text-xs">verkoopt niet zelf</Badge>}
      </div>
      <div className={`grid ${isNB ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-4"} gap-4 mt-4`}>
        {cijfers.map((c, i) => (
          <div key={i}>
            <div className="text-xs text-muted-foreground">{c.label}</div>
            <div className="text-lg font-semibold mt-0.5">{c.val}</div>
            {c.nu !== undefined && c.vor !== undefined && <Verschil nu={c.nu} vorige={c.vor} />}
          </div>
        ))}
      </div>
      {k.kanaal === "frankly" && k.extra?.orders_sample != null && (
        <div className="text-xs text-muted-foreground mt-3">
          Waarvan {k.extra.orders_sample} gratis samples{k.extra.sample_conversie != null ? ` · sample-conversie ${k.extra.sample_conversie}%` : ""}.
        </div>
      )}
      {k.kanaal === "webshop" && k.extra?.actieve_accounts != null && (
        <div className="text-xs text-muted-foreground mt-3">{k.extra.actieve_accounts} actieve webshopaccounts in deze periode.</div>
      )}
      {k.kanaal === "bol" && (k.extra?.top_producten || []).length > 0 && (
        <details className="mt-3">
          <summary className="text-xs text-primary cursor-pointer" data-testid="ecommerce-bol-top">Top 5 producten (productbezoeken)</summary>
          <div className="mt-2 space-y-1">
            {k.extra.top_producten.map((t, i) => (
              <div key={i} className="flex justify-between gap-2 text-xs">
                <span className="truncate">{t.titel}</span>
                <span className="text-muted-foreground shrink-0">{t.bezoeken} bez · {t.orders} ord{t.conversie != null ? ` · ${t.conversie}%` : ""}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function _maandag(iso) {
  const d = new Date(`${iso}T00:00:00`);
  const wd = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - wd);
  return d.toISOString().slice(0, 10);
}
const _MAAND_KORT = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
function _groepKey(iso, groep) {
  if (groep === "jaar") return (iso || "").slice(0, 4);
  if (groep === "maand") return (iso || "").slice(0, 7);
  return _maandag(iso);
}
function _groepLabel(key, groep) {
  if (groep === "jaar") return key;
  if (groep === "maand") { const [y, m] = key.split("-"); return `${_MAAND_KORT[(+m || 1) - 1]} ${y}`; }
  return key;
}
function bouwGrafiek(kanalen, metric, groep = "week") {
  const buckets = {};
  (kanalen || []).forEach((k) => {
    if (!k.gekoppeld) return;
    (k.reeks || []).forEach((r) => {
      const key = _groepKey(r.datum, groep);
      const row = buckets[key] || (buckets[key] = { periode: key, _label: _groepLabel(key, groep) });
      row[k.kanaal] = (row[k.kanaal] || 0) + (r[metric] || 0);
    });
  });
  return Object.values(buckets).sort((a, b) => (a.periode < b.periode ? -1 : 1));
}

function Ecommerce({ merk }) {
  const [periode, setPeriode] = useState("28d");
  const [metric, setMetric] = useState("omzet_excl_btw");
  const [groep, setGroep] = useState("week");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setD(null); setErr(false);
    api.get("/dashboard/ecommerce", { params: { periode, ...(merk ? { merk } : {}) } })
      .then((r) => setD(r.data)).catch(() => setErr(true));
  }, [periode, merk]);

  if (err) return <Empty label="E-commercegegevens konden niet worden geladen." />;
  if (!d) return <div className="text-muted-foreground text-sm">Laden…</div>;

  const totOmzetVorige = (d.kanalen || []).reduce((s, k) => s + (k.vorige?.omzet_excl_btw || 0), 0);
  const totOrdersVorige = (d.kanalen || []).reduce((s, k) => s + (k.vorige?.orders || 0), 0);
  const grafiek = bouwGrafiek(d.kanalen, metric, groep);
  const metricLabel = { omzet_excl_btw: "Omzet excl. btw", orders: "Orders", bezoekers: "Bezoekers" }[metric];
  const groepLabel = { week: "week", maand: "maand", jaar: "jaar" }[groep];
  const zichtbareKanalen = (d.kanalen || []).filter((k) => k.gekoppeld);
  const toonHuidtest = !merk || merk.toLowerCase() === "frankly";

  return (
    <div className="space-y-6" data-testid="ecommerce-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">{d.periode.label} · t/m {d.periode.tot}</div>
        <PeriodeKiezer periode={periode} setPeriode={setPeriode} testid="ecommerce-periode" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs text-muted-foreground">Omzet excl. btw online</div>
          <div className="text-3xl font-semibold mt-1">{fmtEUR(d.totaal.omzet_excl_btw)}</div>
          <Verschil nu={d.totaal.omzet_excl_btw} vorige={totOmzetVorige} />
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs text-muted-foreground">Orders online</div>
          <div className="text-3xl font-semibold mt-1">{(d.totaal.orders || 0).toLocaleString("nl-NL")}</div>
          <Verschil nu={d.totaal.orders} vorige={totOrdersVorige} />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5" data-testid="ecommerce-grafiek">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="font-medium">{metricLabel} per {groepLabel}</div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-full border border-border p-1" data-testid="ecommerce-groep">
              {[["week", "Week"], ["maand", "Maand"], ["jaar", "Jaar"]].map(([g, l]) => (
                <button key={g} onClick={() => setGroep(g)} data-testid={`ecommerce-groep-${g}`}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${groep === g ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
            <div className="inline-flex rounded-full border border-border p-1">
              {[["omzet_excl_btw", "Omzet"], ["orders", "Orders"], ["bezoekers", "Bezoekers"]].map(([m, l]) => (
                <button key={m} onClick={() => setMetric(m)} data-testid={`ecommerce-metric-${m}`}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${metric === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={grafiek} margin={{ left: 4, right: 8, top: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="_label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={48}
              tickFormatter={(v) => (metric === "omzet_excl_btw" ? fmtEUR(v) : v)} />
            <Tooltip formatter={(v, n) => [metric === "omzet_excl_btw" ? fmtEUR2(v) : v, (d.kanalen.find((k) => k.kanaal === n) || {}).naam || n]} />
            <Legend formatter={(n) => (d.kanalen.find((k) => k.kanaal === n) || {}).naam || n} />
            {zichtbareKanalen.map((k) => (
              <Bar key={k.kanaal} dataKey={k.kanaal} stackId="kanalen" fill={KANAAL_KLEUR[k.kanaal]} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(d.kanalen || []).map((k) => <KanaalKaart key={k.kanaal} k={k} />)}
      </div>

      {(d.waarschuwingen || []).length > 0 && (
        <div className="text-xs text-amber-600">{d.waarschuwingen.join(" · ")}</div>
      )}

      {toonHuidtest && (
        <div data-testid="ecommerce-huidtest" className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3"><FlaskConical className="h-4 w-4 text-primary" /><span className="font-medium">Frankly — huidtest</span></div>
          <Huidtest ingebed />
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        Bron per kanaal: bol.com (Retailer API), nextproduct.nl (CRM-webshop), frankly.eu (Shopify), nextbrush.nl (Google Analytics). Bijgewerkt {fmtDateTime(d.bijgewerkt_op)}.
      </div>
    </div>
  );
}

function Huidtest({ merk, ingebed }) {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [periode, setPeriode] = useState("28d");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [kpInput, setKpInput] = useState("");
  const [bezig, setBezig] = useState(false);

  const laad = () => {
    setD(null); setErr("");
    api.get("/dashboard/huidtest", { params: { periode } })
      .then((r) => { setD(r.data); if (r.data?.kosten?.kostprijs != null) setKpInput(String(r.data.kosten.kostprijs)); })
      .catch((e) => { const m = e?.response?.data?.detail || "Kon huidtest niet laden"; setErr(m); toast.error(m); });
  };
  useEffect(laad, [periode]);

  const syncNu = async () => {
    setBezig(true);
    try {
      const { data } = await api.post("/dashboard/huidtest/sync", null, { params: { dagen: 400 } });
      toast.success(`Shopify gesynchroniseerd — ${data.samples ?? 0} samples, ${data.overgeslagen_test ?? 0} testorders overgeslagen.`);
      laad();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync mislukt"); }
    finally { setBezig(false); }
  };

  const saveKostprijs = async () => {
    const v = parseFloat((kpInput || "").replace(",", "."));
    if (isNaN(v) || v < 0) { toast.error("Vul een geldig bedrag in."); return; }
    try {
      await api.put("/dashboard/huidtest/instellingen", { kostprijs: v });
      toast.success("Kostprijs opgeslagen.");
      laad();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };

  const Kop = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="text-sm text-muted-foreground">Alleen Frankly · frankly.eu{d?.periode ? ` · t/m ${d.periode.tot}` : ""}</div>
      <div className="flex flex-wrap items-center gap-2 justify-end">
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={syncNu} disabled={bezig} data-testid="huidtest-sync-btn">
            {bezig ? "Synchroniseren…" : "Shopify nu synchroniseren"}
          </Button>
        )}
        <PeriodeKiezer periode={periode} setPeriode={setPeriode} testid="huidtest-periode" />
      </div>
    </div>
  );

  if (!d && !err) return <div className="space-y-4">{Kop}<div className="text-muted-foreground text-sm">Laden…</div></div>;
  if (d && d.geconfigureerd === false) {
    return (
      <div className="space-y-4">{Kop}
        <div data-testid="huidtest-niet-gekoppeld" className="rounded-lg border border-dashed border-border bg-secondary/40 p-5 text-sm text-muted-foreground">
          Google Analytics is nog niet gekoppeld. Voeg <code>GA4_PROPERTY_ID</code> en <code>GA4_SERVICE_ACCOUNT_JSON_B64</code> toe aan de omgeving (zie de GA4-briefing §3). De rest van het dashboard werkt gewoon.
        </div>
      </div>
    );
  }
  if (err || !d) return <div className="space-y-4">{Kop}<Empty label={err || "Geen gegevens."} /></div>;

  const tr = d.trechter || [];
  const k = d.kosten;
  const ntv = d.nog_te_versturen;
  const dubbel = d.mogelijk_dubbel || [];
  const perHtOrders = d.per_huidtype_orders || [];
  const kleurStap = (bron) => (bron === "shopify" ? "#0F766E" : "#00A5A0");

  return (
    <div className="space-y-5">
      {Kop}
      <div className="flex flex-wrap items-center gap-3">
        <div className="rounded-xl border border-border bg-secondary/30 p-4">
          <div className="text-xs text-muted-foreground">Van test tot sample (winkelwagen)</div>
          <div className="text-2xl font-semibold mt-0.5">
            {d.conversie.eind} van {d.conversie.start} <span className="text-primary">({d.conversie.pct}%)</span>
          </div>
        </div>
      </div>

      <div data-testid="huidtest-bron-notitie" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          Stappen 1-7 komen uit Google Analytics (alleen bezoekers met cookie-toestemming, dus een ondergrens).
          Stappen 8-10 komen uit de échte Shopify-orders van frankly.eu — die kunnen daarom hoger uitvallen dan stap 7.
        </span>
      </div>

      {d.methode === "per_stap" && (
        <div className="text-xs text-muted-foreground">Losse aantallen per stap, geen gesloten trechter.</div>
      )}

      <div data-testid="huidtest-trechter" className="rounded-xl border border-border bg-card p-5">
        <ResponsiveContainer width="100%" height={Math.max(240, tr.length * 40)}>
          <BarChart data={tr} layout="vertical" margin={{ left: 8, right: 44 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
            <YAxis type="category" dataKey="naam" width={170} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, n, p) => [`${v} ${p.payload.bron === "shopify" ? "orders" : "bezoekers"} · ${p.payload.pct_van_start}% van start`, p.payload.naam]} />
            <Bar dataKey="gebruikers" radius={[0, 4, 4, 0]}>
              {tr.map((s) => <Cell key={s.stap} fill={kleurStap(s.bron)} />)}
              <LabelList dataKey="gebruikers" position="right" style={{ fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: "#00A5A0" }} /> Google Analytics (1-7)</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: "#0F766E" }} /> Shopify-orders (8-10)</span>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 pr-2 font-medium">Stap</th><th className="py-2 px-2 font-medium text-right">Aantal</th>
              <th className="py-2 px-2 font-medium text-right">% van start</th><th className="py-2 pl-2 font-medium text-right">Afgehaakt</th>
            </tr></thead>
            <tbody>
              {tr.map((s) => (
                <tr key={s.stap} className="border-b border-border/50">
                  <td className="py-2 pr-2">{s.stap}. {s.naam}{s.bron === "shopify" ? <span className="ml-1.5 text-[10px] uppercase tracking-wide text-teal-700">Shopify</span> : ""}</td>
                  <td className="py-2 px-2 text-right font-medium">{s.gebruikers}</td>
                  <td className="py-2 px-2 text-right text-muted-foreground">{s.pct_van_start}%</td>
                  <td className="py-2 pl-2 text-right text-muted-foreground">{s.afhakers == null ? "—" : s.afhakers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Kostenblok */}
      {k && (
        <div data-testid="huidtest-kosten" className="rounded-xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="font-medium text-sm flex items-center gap-2"><Euro className="h-4 w-4 text-primary" /> Kosten en opbrengst van samples</div>
            {isAdmin ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Kostprijs/sample €</span>
                <input data-testid="huidtest-kostprijs-input" value={kpInput} onChange={(e) => setKpInput(e.target.value)}
                  className="w-20 rounded-md border border-border bg-background px-2 py-1 text-sm" inputMode="decimal" />
                <Button size="sm" variant="outline" onClick={saveKostprijs} data-testid="huidtest-kostprijs-save">Opslaan</Button>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">Kostprijs/sample {fmtEUR2(k.kostprijs)}</span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <KostStat label="Verstuurd" waarde={k.aantal_verstuurd} testid="kost-verstuurd" />
            <KostStat label="Totale kosten" waarde={fmtEUR2(k.totale_kosten)} testid="kost-totaal" />
            <KostStat label="Betaalde orders na sample" waarde={k.betaalde_orders} testid="kost-betaalde-orders" />
            <KostStat label="Omzet excl. btw" waarde={fmtEUR2(k.omzet_excl_btw)} testid="kost-omzet" />
            <KostStat label="Gewonnen klanten" waarde={k.gewonnen_klanten} testid="kost-gewonnen" />
            <KostStat label="Kosten per gewonnen klant" waarde={k.kosten_per_klant == null ? "—" : fmtEUR2(k.kosten_per_klant)} testid="kost-per-klant" />
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">Kosten = aantal verstuurde samples × kostprijs. Een betaalde vervolgorder telt als die binnen 60 dagen na verzending op hetzelfde e-mailadres binnenkomt.</div>
        </div>
      )}

      {/* Per huidtype (stappen 8-10) + apparaat */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div data-testid="huidtest-per-huidtype-orders" className="rounded-xl border border-border bg-card p-5">
          <div className="font-medium mb-2 text-sm">Samples per huidtype (besteld → verstuurd → klant)</div>
          {perHtOrders.length === 0 ? <Empty label="Nog geen sample-orders in deze periode." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1.5 pr-2 font-medium">Huidtype</th>
                  <th className="py-1.5 px-2 font-medium text-right">Besteld</th>
                  <th className="py-1.5 px-2 font-medium text-right">Verstuurd</th>
                  <th className="py-1.5 pl-2 font-medium text-right">Klant</th>
                </tr></thead>
                <tbody>
                  {perHtOrders.map((h) => (
                    <tr key={h.huidtype} className="border-b border-border/50">
                      <td className="py-1.5 pr-2">{h.huidtype}</td>
                      <td className="py-1.5 px-2 text-right font-medium">{h.besteld}</td>
                      <td className="py-1.5 px-2 text-right">{h.verstuurd}</td>
                      <td className="py-1.5 pl-2 text-right">{h.gewonnen}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="font-medium mb-2 text-sm">Samples per huidtype (test, GA)</div>
          {(d.per_huidtype || []).length === 0 ? <Empty label="Nog geen samples." /> : (
            <table className="w-full text-sm">
              <tbody>
                {d.per_huidtype.map((h) => (
                  <tr key={h.huidtype} className="border-b border-border/50">
                    <td className="py-1.5">{h.label}</td>
                    <td className="py-1.5 text-right font-medium">{h.samples}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="font-medium mt-4 mb-1 text-sm">Per apparaat</div>
          {(d.per_apparaat || []).length === 0 ? <Empty label="Geen gegevens." /> : (
            <div className="text-sm text-muted-foreground">
              {d.per_apparaat.map((a) => `${a.label} ${a.eind} van ${a.start}`).join(" · ")}
            </div>
          )}
        </div>
      </div>

      {/* Signalen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div data-testid="huidtest-nog-te-versturen" className="rounded-xl border border-border bg-card p-5">
          <div className="font-medium mb-2 text-sm flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> Nog te versturen</div>
          {!ntv || ntv.aantal === 0 ? (
            <div className="text-sm text-muted-foreground">Geen openstaande sample-orders.</div>
          ) : (
            <div className="flex items-baseline gap-3">
              <div className="text-3xl font-semibold">{ntv.aantal}</div>
              <div className="text-sm text-muted-foreground">open sample-order{ntv.aantal === 1 ? "" : "s"}{ntv.oudste_dagen != null ? ` · oudste staat ${ntv.oudste_dagen} dag${ntv.oudste_dagen === 1 ? "" : "en"} open` : ""}</div>
            </div>
          )}
          <div className="text-[11px] text-muted-foreground mt-2">Open = in Shopify nog niet afgehandeld (fulfilled). Labels print je in Shopify zelf.</div>
        </div>

        <div data-testid="huidtest-mogelijk-dubbel" className="rounded-xl border border-border bg-card p-5">
          <div className="font-medium mb-2 text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Mogelijk dubbel (90 dagen)</div>
          {dubbel.length === 0 ? (
            <div className="text-sm text-muted-foreground">Geen dubbele aanvragen gevonden.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {dubbel.map((g, i) => (
                <div key={i} className="rounded-lg border border-border/60 bg-secondary/30 p-2.5 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium break-all">{g.waarde}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{g.type} · {g.aantal}×</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {g.orders.map((o) => `${o.naam} (${o.datum}${o.huidtype ? `, ${o.huidtype}` : ""})`).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-muted-foreground mt-2">Zelfde e-mail of postcode + huisnummer. Alleen ter info — niets wordt geblokkeerd.</div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        Trechter 1-7: Google Analytics (alleen bezoekers met analytics-toestemming; echte aantallen liggen hoger). Trechter 8-10 + kosten: echte Shopify-orders van frankly.eu.{d.uit_cache ? " (GA uit cache)" : ""}{d.ga4_fout === "verouderd" ? " (GA verouderd)" : ""} Laatste Shopify-sync: {d.laatste_sync ? fmtDateTime(d.laatste_sync) : "—"}. Bijgewerkt {fmtDateTime(d.bijgewerkt_op)}.
      </div>
    </div>
  );
}

function KostStat({ label, waarde, testid }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3" data-testid={testid}>
      <div className="text-[11px] text-muted-foreground leading-tight">{label}</div>
      <div className="text-lg font-semibold mt-0.5 tabular-nums">{waarde}</div>
    </div>
  );
}


export default function Dashboard() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("algemeen");
  const [merk, setMerk] = useState("");
  const [merken, setMerken] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [kleurenOpen, setKleurenOpen] = useState(false);

  useEffect(() => {
    api.get("/producten/merken").then((r) => setMerken(r.data || [])).catch(() => {});
    api.get("/instellingen/klant-kleuren").then((r) => setOverrides(r.data?.overrides || {})).catch(() => {});
  }, []);
  useEffect(() => {
    setData(null);
    api.get("/dashboard", { params: merk ? { merk } : {} }).then((r) => setData(r.data)).catch(() => {});
  }, [merk]);

  const klantKleur = useMemo(() => bouwKlantKleurMap(data?.algemeen || {}, overrides), [data, overrides]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-sm text-muted-foreground">
            Overzicht · {data?.jaar} (YTD){merk ? ` · alleen ${merk}` : ""}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-1">Dashboard</h1>
        </div>
        {merken.length > 1 && (
          <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="dash-merk-schakelaar">
            {[{ merk: "", label: "Alles" }, ...merken.map((m) => ({ merk: m.merk, label: m.merk }))].map((m) => (
              <button key={m.merk || "alles"} onClick={() => setMerk(m.merk)}
                data-testid={`dash-merk-${m.merk || "alles"}`}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
                  merk === m.merk ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`dash-tab-${key}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
              tab === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="text-muted-foreground text-sm">Laden…</div>
      ) : (
        <>
          {tab === "algemeen" && <Algemeen d={data.algemeen} jaar={data.jaar} vw={data.voorraad_waarde} merk={merk} klantKleur={klantKleur} onKleuren={() => setKleurenOpen(true)} />}
          {tab === "facturatie" && <Facturatie d={data.facturatie} />}
          {tab === "acquisitie" && <Acquisitie d={data.acquisitie} belVandaag={data.belletjes_vandaag} />}
          {tab === "ecommerce" && <Ecommerce merk={merk} />}
          {tab === "logistiek" && <Logistiek d={data.logistiek} />}
          {tab === "historisch" && <Historisch merk={merk} />}
          {tab === "huidtest" && <Huidtest merk={merk} />}

          {tab === "algemeen" && (
            <Panel title="Laatste activiteiten" action={<Link to="/activiteiten" className="text-sm text-primary hover:underline">Alle bekijken →</Link>} testid="dash-activiteiten">
              <div className="divide-y divide-border -mx-2">
                {(data.laatste_activiteiten || []).map((a) => (
                  <Link key={a.id} to={`/organisaties/${a.organisatie_id}`}
                    className="flex items-center justify-between gap-4 p-3 rounded-lg hover:bg-secondary transition-colors"
                    data-testid={`act-row-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">{a.type}</Badge>
                        <span className="font-medium truncate">{a.onderwerp}</span>
                      </div>
                      <div className="text-sm text-muted-foreground mt-0.5 truncate">{a.organisatie_naam}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {a.status === "open"
                        ? <Badge className="bg-accent text-accent-foreground hover:bg-accent">open</Badge>
                        : <Badge variant="secondary">afgerond</Badge>}
                      <span className="text-xs text-muted-foreground">{fmtDateTime(a.datum || a.created_at)}</span>
                    </div>
                  </Link>
                ))}
                {(!data.laatste_activiteiten || data.laatste_activiteiten.length === 0) && <Empty label="Nog geen activiteiten." />}
              </div>
            </Panel>
          )}
        </>
      )}

      <KlantKleurenDialog
        open={kleurenOpen}
        onClose={() => setKleurenOpen(false)}
        d={data?.algemeen}
        overrides={overrides}
        onSaved={setOverrides}
      />
    </div>
  );
}
